import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, PackageCheck, ChevronDown, ChevronRight } from "lucide-react";

const STATUS_OPTS = ["placed", "shipped", "arrived"];
const statusCls = {
  placed: "bg-amber-100 text-amber-700",
  shipped: "bg-sky-100 text-sky-700",
  arrived: "bg-emerald-100 text-emerald-700",
};

// Tracking IDs registry for ONE order. Order -> many trackings; each tracking has
// exactly one warehouse code + its own optional CBM. Registering a tracking never
// splits/duplicates order quantities.
export function TrackingPanel({ orderId, source = "", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ tracking_code: "", warehouse_code: "", cbm: "" });

  const load = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/trackings?order_id=${encodeURIComponent(orderId)}`);
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load trackings");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, orderId]);

  const add = async () => {
    if (!draft.tracking_code.trim()) return toast.error("Tracking ID is required");
    try {
      const body = {
        order_id: orderId, source,
        tracking_code: draft.tracking_code.trim(),
        warehouse_code: draft.warehouse_code.trim(),
      };
      if (draft.cbm !== "") body.cbm = parseFloat(draft.cbm);
      await api.post("/trackings", body);
      toast.success("Tracking added");
      setDraft({ tracking_code: "", warehouse_code: "", cbm: "" });
      setAdding(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };

  const patch = async (id, field, value) => {
    try {
      const body = {};
      if (field === "cbm") body.cbm = value === "" ? null : parseFloat(value);
      else body[field] = value;
      if (field === "cbm" && value === "") return; // don't null via blank blur
      await api.patch(`/trackings/${id}`, body);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (id, code) => {
    if (!window.confirm(`Delete tracking ${code}? (order quantities are not affected)`)) return;
    try {
      await api.delete(`/trackings/${id}`);
      toast.success("Tracking deleted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!orderId) return null;

  return (
    <div className="border-t border-slate-100 bg-slate-50/60" data-testid={`tracking-panel-${orderId}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100"
        data-testid={`tracking-toggle-${orderId}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <PackageCheck className="w-3.5 h-3.5 text-slate-500" />
        Tracking IDs {rows.length > 0 && <span className="ml-0.5 font-mono">({rows.length})</span>}
        <span className="text-slate-400 font-normal">· one order → many trackings, each with its own WH code + CBM</span>
      </button>

      {open && (
        <div className="px-4 pb-3">
          <table className="excel-table w-full text-xs" data-testid={`tracking-table-${orderId}`}>
            <thead><tr>
              <th>Tracking ID</th>
              <th className="w-32">Warehouse Code</th>
              <th className="text-right w-24">CBM</th>
              <th className="w-28">Logistics</th>
              <th className="w-24">Allocation</th>
              <th className="w-10"></th>
            </tr></thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} data-testid={`tracking-row-${t.id}`}>
                  <td>
                    <Input className="h-7 w-40 font-mono text-xs" defaultValue={t.tracking_code}
                      data-testid={`tracking-code-${t.id}`}
                      onBlur={e => { const v = e.target.value.trim(); if (v && v !== t.tracking_code) patch(t.id, "tracking_code", v); }} />
                  </td>
                  <td>
                    <Input className="h-7 w-28 font-mono text-xs" defaultValue={t.warehouse_code || ""}
                      placeholder="—" data-testid={`tracking-wh-${t.id}`}
                      onBlur={e => { const v = e.target.value.trim(); if (v !== (t.warehouse_code || "")) patch(t.id, "warehouse_code", v); }} />
                  </td>
                  <td className="text-right">
                    <Input type="number" step="0.001" className="h-7 w-20 font-mono text-xs text-right"
                      defaultValue={t.cbm ?? ""} placeholder="—" data-testid={`tracking-cbm-${t.id}`}
                      onBlur={e => { const raw = e.target.value.trim(); if (raw !== "" && parseFloat(raw) !== t.cbm) patch(t.id, "cbm", raw); }} />
                  </td>
                  <td>
                    <select className="h-7 text-xs rounded border border-slate-200 bg-white px-1"
                      value={t.logistics_status} data-testid={`tracking-status-${t.id}`}
                      onChange={e => patch(t.id, "logistics_status", e.target.value)}>
                      {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${t.allocation_status === "allocated" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                      data-testid={`tracking-alloc-${t.id}`}>
                      {t.allocation_status === "allocated" ? "Allocated" : "Pending"}
                    </span>
                  </td>
                  <td className="text-right">
                    <button onClick={() => remove(t.id, t.tracking_code)} className="text-rose-500 hover:text-rose-700"
                      data-testid={`tracking-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !adding && (
                <tr><td colSpan={6} className="text-center text-slate-400 py-2">No tracking IDs yet for this order.</td></tr>
              )}
              {adding && (
                <tr className="bg-sky-50" data-testid={`tracking-add-row-${orderId}`}>
                  <td><Input autoFocus className="h-7 w-40 font-mono text-xs" placeholder="Tracking ID"
                    value={draft.tracking_code} data-testid={`tracking-new-code-${orderId}`}
                    onChange={e => setDraft({ ...draft, tracking_code: e.target.value })}
                    onKeyDown={e => { if (e.key === "Enter") add(); }} /></td>
                  <td><Input className="h-7 w-28 font-mono text-xs" placeholder="WH code"
                    value={draft.warehouse_code} data-testid={`tracking-new-wh-${orderId}`}
                    onChange={e => setDraft({ ...draft, warehouse_code: e.target.value })} /></td>
                  <td className="text-right"><Input type="number" step="0.001" className="h-7 w-20 font-mono text-xs text-right"
                    placeholder="CBM" value={draft.cbm} data-testid={`tracking-new-cbm-${orderId}`}
                    onChange={e => setDraft({ ...draft, cbm: e.target.value })} /></td>
                  <td colSpan={3}>
                    <Button size="sm" className="h-7 bg-sky-600 hover:bg-sky-700" onClick={add} data-testid={`tracking-add-confirm-${orderId}`}>Save</Button>
                    <Button size="sm" variant="ghost" className="h-7 ml-1" onClick={() => { setAdding(false); setDraft({ tracking_code: "", warehouse_code: "", cbm: "" }); }}>Cancel</Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {!adding && (
            <Button size="sm" variant="outline" className="mt-2 h-7 border-sky-300 text-sky-700 hover:bg-sky-50"
              onClick={() => setAdding(true)} data-testid={`tracking-add-btn-${orderId}`}>
              <Plus className="w-3.5 h-3.5 mr-1" />Add Tracking ID
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
